import { Badge, Box, Card, Group, Stack, Text } from '@mantine/core'
import type { MaintenanceLogWithRelations } from '@markku/types'
import { formatDate, getCategoryColor, getCategoryLabel } from '@markku/utils'
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconUser,
} from '@tabler/icons-react'

interface LogSummaryCardProps {
  log: MaintenanceLogWithRelations
}

export function LogSummaryCard({ log }: LogSummaryCardProps) {
  const completedDate = log.completedDate ? new Date(log.completedDate) : null
  const dayName = completedDate?.toLocaleDateString('fi-FI', {
    weekday: 'short',
  })
  const dateStr = log.completedDate ? formatDate(log.completedDate) : '-'

  const category = log.maintenanceTask?.category
  const duration = (
    log.maintenanceTask as { estimatedDurationMinutes?: number }
  )?.estimatedDurationMinutes

  return (
    <Card withBorder>
      <Text size="lg" fw={600} mb="md">
        Huoltotehtävän tiedot
      </Text>

      <Stack gap="sm">
        {/* Completion date */}
        <Group gap="sm">
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-gray-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconCalendar size={18} color="var(--mantine-color-gray-6)" />
          </Box>
          <Stack gap={0}>
            <Text size="sm" fw={500}>
              Suoritettu
            </Text>
            <Text size="sm" c="dimmed">
              {dayName} {dateStr}
            </Text>
          </Stack>
        </Group>

        {/* Category and duration */}
        <Group gap="sm">
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'var(--mantine-color-gray-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconCheck size={18} color="var(--mantine-color-gray-6)" />
          </Box>
          <Stack gap={0} style={{ flex: 1 }}>
            <Text size="sm" fw={500}>
              Tehtävän tyyppi
            </Text>
            <Group gap="xs" mt={4}>
              {category && (
                <Badge
                  size="sm"
                  variant="light"
                  color={getCategoryColor(category)}
                >
                  {getCategoryLabel(category)}
                </Badge>
              )}
              {duration && (
                <Group gap={4}>
                  <IconClock size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dimmed">
                    {duration} min
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>
        </Group>

        {/* Completed by */}
        {log.completedByUser && (
          <Group gap="sm">
            <Box
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-gray-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconUser size={18} color="var(--mantine-color-gray-6)" />
            </Box>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                Suorittaja
              </Text>
              <Text size="sm" c="dimmed">
                {log.completedByUser.firstName} {log.completedByUser.lastName}
              </Text>
            </Stack>
          </Group>
        )}

        {/* Description */}
        {log.descriptionSnapshot && (
          <>
            <Box
              style={{
                height: 1,
                backgroundColor: 'var(--mantine-color-gray-2)',
                margin: 'var(--mantine-spacing-sm) 0',
              }}
            />
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Kuvaus
              </Text>
              <Text size="sm" c="dimmed">
                {log.descriptionSnapshot}
              </Text>
            </Stack>
          </>
        )}

        {/* User notes */}
        {log.notes && (
          <>
            <Box
              style={{
                height: 1,
                backgroundColor: 'var(--mantine-color-gray-2)',
                margin: 'var(--mantine-spacing-sm) 0',
              }}
            />
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Huomiot
              </Text>
              <Text size="sm" c="dimmed" fs="italic">
                "{log.notes}"
              </Text>
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  )
}
